import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { IMediaBankFile, IMediaBankFileReference } from '../../../../../shared/media-bank';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { isDefined } from '@fi-shared/utils';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiSiteDesignHelperService } from '../../../../fi-site-design-helper.service';
import { FileType, FiUploadType, ObjectTypes } from '@fi-shared/types';
import { getAcceptByFileType } from '@fi-shared/utils/get-accept-by-file-type';
import { IInsertMediaResult, IInsertMediaUploadItem } from '@fi-shared/fi-insert-media/fi-insert-media';
import { FiInsertMedia2Service } from '@fi-shared/fi-insert-media/fi-insert-media2.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import {
    ISiteDesignSettingLoginImagesData,
    SiteDesignSettingLoginImagesType,
} from '../fi-brand-editor-images-card.component';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';

@Component({
    selector: 'fi-brand-image-editor',
    templateUrl: './fi-brand-image-editor.component.html',
    styleUrls: ['./fi-brand-image-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-brand-image-editor' },
    standalone: false,
})
export class FiBrandImageEditorComponent {
    @Input() logoReference: IMediaBankFileReference;
    @Output() logoReferenceChange = new EventEmitter<IMediaBankFileReference>();

    @Input() landscapeReference: IMediaBankFileReference;
    @Output() landscapeReferenceChange = new EventEmitter<IMediaBankFileReference>();

    @Input() loginSettingsImageData: ISiteDesignSettingLoginImagesData;

    readonly acceptFileType: string = getAcceptByFileType([FileType.Image], undefined);

    constructor(
        public elementRef: ElementRef,
        private fileUploadService: FiFileUpload2Service,
        private fiInsertMedia2Service: FiInsertMedia2Service,
        private mediaStorageService: FiMediaStorageService,
        private mediaBankHelperService: MediaBankHelperService,
        private appTextService: AppTextService,
        private fiSiteDesignHelperService: FiSiteDesignHelperService,
        private loadingService: LoadingService,
    ) {}

    getImageSrcByType(): string {
        const currentImageReference = this.getCurrentImageReference();

        if (!isDefined(currentImageReference)) return;

        return this.mediaStorageService.getMediaFileUrl(currentImageReference, {
            size: this.loginSettingsImageData.imageSize,
        });
    }

    getImageContainerHeader(): string {
        return this.appTextService.get(
            this.isImageLogoType() ? 'customer_settings.logo' : 'site_design.background_image',
        );
    }

    removeImage() {
        this.setLoginImageReference(undefined);
    }

    openInsertMediaDialog() {
        this.fiInsertMedia2Service
            .openInsertMediaDialogWithFileSizes(ObjectTypes.CustomerSetting, {
                multiple: false,
                accept: this.acceptFileType,
                fileTypes: [FileType.Image],
            })
            .onYesButtonClick((result: IInsertMediaResult) => {
                this.setLoginImageReference(result.mediaBankFileReferences[0]);
            });
    }

    onFileInputChange($event: Event) {
        if ($event.target && ($event.target as HTMLInputElement).files.length) {
            this.uploadFile(($event.target as HTMLInputElement).files[0]);
        }
    }

    uploadFile(file: File) {
        if (!file.type.includes('image/')) {
            return;
        }

        let uploadedMediaBankFile: IMediaBankFile;
        this.loadingService
            .trackPromise(
                this.fileUploadService.uploadFiles({
                    files: [file],
                    onUploadStarted: (uploadItem: IInsertMediaUploadItem) =>
                        (uploadedMediaBankFile = uploadItem.mediaBankFile),
                    skipSaveMediaBankOnBackend: true,
                    formData: {
                        uploadType: this.isImageLogoType() ? FiUploadType.Logo : FiUploadType.Background,
                    },
                }),
            )
            .then(() => {
                const newLoginImageReference = this.mediaBankHelperService.getMediaBankFileReferenceFromMediaFile(
                    uploadedMediaBankFile,
                    {
                        isNewUpload: true,
                        showInMediaBank: false,
                    },
                );
                this.setLoginImageReference(newLoginImageReference);
            });
    }

    private setLoginImageReference(newReference: IMediaBankFileReference) {
        if (this.isImageLogoType()) {
            this.logoReference = newReference;
            this.logoReferenceChange.emit(newReference);
        } else {
            this.landscapeReference = newReference;
            this.landscapeReferenceChange.emit(newReference);
        }
        this.fiSiteDesignHelperService.setBrandSettingsChanged(true);
    }

    private isImageLogoType(): boolean {
        return this.loginSettingsImageData.type === SiteDesignSettingLoginImagesType.Logo;
    }

    private getCurrentImageReference(): IMediaBankFileReference {
        return this.isImageLogoType() ? this.logoReference : this.landscapeReference;
    }
}
